import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Stack, Button, Grid, TextField,
  MenuItem, IconButton, Switch, FormControlLabel, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { invoiceSchema, issuerSchema, organisationSchema } from '../../validation/invoiceSchema';
import apiClient from '../../apiClient';
import InvoiceService from '../../services/InvoiceService';
import InvoiceIssuerService from '../../services/InvoiceIssuerService';
import InvoiceOrganisationService from '../../services/InvoiceOrganisationService';
import InvoicePdfDocument from '../../components/invoice/InvoicePdfDocument';
import IssuerProfileForm from '../../components/invoice/IssuerProfileForm';
import OrganisationForm from '../../components/invoice/OrganisationForm';
import { usePostHog } from '@posthog/react';

const invoiceService = new InvoiceService();
const issuerService = new InvoiceIssuerService();
const orgService = new InvoiceOrganisationService();

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const issuerDefaultValues = {
  name: '',
  businessName: '',
  abn: '',
  email: '',
  phone: '',
  address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
  bankDetails: { bankName: '', bsb: '', accountNumber: '', accountName: '' },
  paymentTerms: '',
};

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const posthog = usePostHog();

  const [issuers, setIssuers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [issuerDialogOpen, setIssuerDialogOpen] = useState(false);
  const [issuerDialogRecord, setIssuerDialogRecord] = useState(null);
  const [issuerLogoPreview, setIssuerLogoPreview] = useState(null);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [dialogSaving, setDialogSaving] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: '',
      issuerId: '',
      organisationId: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      lineItems: [{ description: '', quantity: 1, unitPrice: 0 }],
      gstIncluded: false,
      notes: '',
      templateId: 'classic',
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });

  // Inline issuer dialog form
  const {
    control: issuerControl,
    handleSubmit: handleIssuerSubmit,
    reset: resetIssuer,
    getValues: getIssuerValues,
    formState: { errors: issuerErrors },
  } = useForm({
    resolver: yupResolver(issuerSchema),
    defaultValues: issuerDefaultValues,
  });

  // Inline org dialog form
  const {
    control: orgControl,
    handleSubmit: handleOrgSubmit,
    reset: resetOrg,
    formState: { errors: orgErrors },
  } = useForm({
    resolver: yupResolver(organisationSchema),
    defaultValues: {
      name: '', abn: '', email: '', phone: '', contactPerson: '',
      address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
    },
  });

  const watchLineItems = watch('lineItems');
  const watchGstIncluded = watch('gstIncluded');
  const watchIssuerId = watch('issuerId');
  const watchOrgId = watch('organisationId');
  const watchTemplateId = watch('templateId');

  const resetIssuerDialog = useCallback(() => {
    setIssuerDialogOpen(false);
    setIssuerDialogRecord(null);
    setIssuerLogoPreview(null);
    resetIssuer(issuerDefaultValues);
  }, [resetIssuer]);

  const openIssuerDialog = useCallback(() => {
    setError(null);
    setIssuerDialogRecord(null);
    setIssuerLogoPreview(null);
    resetIssuer(issuerDefaultValues);
    setIssuerDialogOpen(true);
  }, [resetIssuer]);

  const handleIssuerLogoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    try {
      let issuerId = issuerDialogRecord?.id;
      if (!issuerId) {
        const currentValues = getIssuerValues();
        const created = await issuerService.create({
          ...issuerDefaultValues,
          ...currentValues,
          name: currentValues.name || 'My Business',
          email: currentValues.email || 'update@email.com',
        });
        issuerId = created.id;
        setIssuerDialogRecord(created);
      }

      await issuerService.uploadLogo(issuerId, file);
      setIssuerLogoPreview(URL.createObjectURL(file));
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
    }
  }, [getIssuerValues, issuerDialogRecord]);

  const handleCopyFromProfile = useCallback(async () => {
    setError(null);
    try {
      let profileData = null;

      try {
        const companyProfile = await apiClient.get('/company-profiles/me');
        if (companyProfile && (companyProfile.companyName || companyProfile.email)) {
          profileData = {
            name: companyProfile.companyName || '',
            businessName: companyProfile.companyName || '',
            abn: companyProfile.abn || '',
            email: companyProfile.email || '',
            phone: companyProfile.phone || '',
            address: {
              street: '',
              city: companyProfile.location || '',
              state: '',
              postcode: '',
              country: 'Australia',
            },
          };
        }
      } catch (_) {
        // no company profile
      }

      if (!profileData) {
        try {
          const jsProfile = await apiClient.get('/job-seeker-profiles/me');
          const basicProfile = await apiClient.get('/profiles/me');
          if (jsProfile || basicProfile) {
            const firstName = basicProfile?.firstName || '';
            const lastName = basicProfile?.lastName || '';
            profileData = {
              name: [firstName, lastName].filter(Boolean).join(' ') || '',
              businessName: '',
              abn: jsProfile?.abnNumber || '',
              email: basicProfile?.email || '',
              phone: basicProfile?.phone || '',
              address: {
                street: '',
                city: jsProfile?.suburb || jsProfile?.location || '',
                state: jsProfile?.state || '',
                postcode: jsProfile?.postcode || '',
                country: 'Australia',
              },
            };
          }
        } catch (_) {
          // no jobseeker profile
        }
      }

      if (profileData) {
        const currentValues = getIssuerValues();
        resetIssuer({
          ...currentValues,
          name: profileData.name || currentValues.name || '',
          businessName: profileData.businessName || currentValues.businessName || '',
          abn: profileData.abn || currentValues.abn || '',
          email: profileData.email || currentValues.email || '',
          phone: profileData.phone || currentValues.phone || '',
          address: {
            ...(currentValues.address || issuerDefaultValues.address),
            street: profileData.address?.street || currentValues.address?.street || '',
            city: profileData.address?.city || currentValues.address?.city || '',
            state: profileData.address?.state || currentValues.address?.state || '',
            postcode: profileData.address?.postcode || currentValues.address?.postcode || '',
            country: profileData.address?.country || currentValues.address?.country || 'Australia',
          },
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile data');
    }
  }, [getIssuerValues, resetIssuer]);

  const handleIssuerCreate = handleIssuerSubmit(async (data) => {
    setDialogSaving(true);
    setError(null);
    try {
      const saved = issuerDialogRecord
        ? await issuerService.update(issuerDialogRecord.id, data)
        : await issuerService.create(data);
      const refreshed = await issuerService.getAll();
      setIssuers(refreshed);
      setValue('issuerId', saved.id);
      resetIssuerDialog();
    } catch (err) {
      setError(err.message || 'Failed to create billing profile');
    } finally {
      setDialogSaving(false);
    }
  });

  // Calculate totals
  const subtotal = (watchLineItems || []).reduce((sum, item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    return sum + qty * price;
  }, 0);
  const gstAmount = watchGstIncluded ? Math.round(subtotal * 0.1 * 100) / 100 : 0;
  const total = Math.round((subtotal + gstAmount) * 100) / 100;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [issuerData, orgData] = await Promise.all([
        issuerService.getAll(),
        orgService.getAll(),
      ]);
      setIssuers(issuerData);
      setOrganisations(orgData);

      if (isEdit) {
        const invoice = await invoiceService.getById(id);
        reset({
          invoiceNumber: invoice.invoiceNumber,
          issuerId: invoice.issuerId,
          organisationId: invoice.organisationId,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          lineItems: invoice.lineItems,
          gstIncluded: invoice.gstIncluded,
          notes: invoice.notes || '',
          templateId: invoice.templateId || 'classic',
        });
      } else {
        const nextNum = await invoiceService.getNextNumber();
        setValue('invoiceNumber', nextNum);
        if (issuerData.length > 0) {
          const defaultIssuer = issuerData.find((i) => i.isDefault) || issuerData[0];
          setValue('issuerId', defaultIssuer.id);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, reset, setValue]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSubmit = async (data, asDraft = true) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...data,
        lineItems: data.lineItems.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          amount: Math.round(Number(item.quantity) * Number(item.unitPrice) * 100) / 100,
        })),
        gstRate: data.gstIncluded ? 10 : 0,
      };

      if (isEdit) {
        await invoiceService.update(id, payload);
      } else {
        const created = await invoiceService.create(payload);
        posthog?.capture('invoice_created', { invoice_id: created?.id, status: asDraft ? 'draft' : 'pending', line_items: payload.lineItems?.length });
        if (!asDraft) {
          await invoiceService.updateStatus(created.id, 'pending');
        }
      }
      navigate('/invoice');
    } catch (err) {
      setError(err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = handleSubmit((data) => onSubmit(data, true));
  const handleIssue = handleSubmit((data) => onSubmit(data, false));

  // Build preview data
  const selectedIssuer = issuers.find((i) => i.id === watchIssuerId);
  const selectedOrg = organisations.find((o) => o.id === watchOrgId);
  const previewData = {
    invoiceNumber: watch('invoiceNumber'),
    issueDate: watch('issueDate'),
    dueDate: watch('dueDate'),
    lineItems: (watchLineItems || []).map((item) => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      amount: Math.round((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) * 100) / 100,
    })),
    subtotal,
    gstIncluded: watchGstIncluded,
    gstRate: watchGstIncluded ? 10 : 0,
    gstAmount,
    total,
    notes: watch('notes'),
    templateId: watchTemplateId,
    currency: 'AUD',
    issuerSnapshot: selectedIssuer,
    organisationSnapshot: selectedOrg,
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/invoice')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            {isEdit ? 'Edit Invoice' : 'New Invoice'}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {issuers.length === 0 && (
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/invoice/profile')}>
              Set Up
            </Button>
          }>
            You need to create a billing profile before creating invoices.
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={3}>
            {/* Invoice Number & Template */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="invoiceNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Invoice Number"
                      fullWidth
                      error={!!errors.invoiceNumber}
                      helperText={errors.invoiceNumber?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="templateId"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select label="Template" fullWidth>
                      <MenuItem value="classic">Classic</MenuItem>
                      <MenuItem value="modern">Modern</MenuItem>
                      <MenuItem value="detailed">Detailed</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>

            {/* Issuer & Organisation */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <Controller
                    name="issuerId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="From (Billing Profile)"
                        fullWidth
                        error={!!errors.issuerId}
                        helperText={errors.issuerId?.message || (issuers.length === 0 ? 'No billing profiles yet' : '')}
                      >
                        {issuers.map((iss) => (
                          <MenuItem key={iss.id} value={iss.id}>
                            {iss.businessName || iss.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {issuers.length === 0 && (
                    <IconButton
                      color="primary"
                      onClick={openIssuerDialog}
                      sx={{ mt: 0.5 }}
                      title="Create billing profile"
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <Controller
                    name="organisationId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="To (Organisation)"
                        fullWidth
                        error={!!errors.organisationId}
                        helperText={errors.organisationId?.message || (organisations.length === 0 ? 'No organisations yet' : '')}
                      >
                        {organisations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => { resetOrg(); setOrgDialogOpen(true); }}
                    sx={{ mt: 0.5 }}
                    title="Create organisation"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>

            {/* Dates */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="issueDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      label="Issue Date"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={!!errors.issueDate}
                      helperText={errors.issueDate?.message}
                      sx={{ '& input[type="date"]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      label="Due Date"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={!!errors.dueDate}
                      helperText={errors.dueDate?.message}
                      sx={{ '& input[type="date"]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider />

            {/* Line Items */}
            <Box>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Line Items
              </Typography>
              <Stack spacing={2}>
                {fields.map((field, index) => (
                  <Grid container spacing={1} key={field.id} alignItems="flex-start">
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <Controller
                        name={`lineItems.${index}.description`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            label="Description"
                            fullWidth
                            size="small"
                            error={!!errors.lineItems?.[index]?.description}
                            helperText={errors.lineItems?.[index]?.description?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 2 }}>
                      <Controller
                        name={`lineItems.${index}.quantity`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            label="Qty"
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                            error={!!errors.lineItems?.[index]?.quantity}
                            helperText={errors.lineItems?.[index]?.quantity?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 2 }}>
                      <Controller
                        name={`lineItems.${index}.unitPrice`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            label="Unit Price"
                            fullWidth
                            size="small"
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              },
                              htmlInput: { inputMode: 'decimal' },
                            }}
                            error={!!errors.lineItems?.[index]?.unitPrice}
                            helperText={errors.lineItems?.[index]?.unitPrice?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 3, sm: 2 }}>
                      <TextField
                        label="Amount"
                        value={formatCurrency(
                          (Number(watchLineItems?.[index]?.quantity) || 0) *
                          (Number(watchLineItems?.[index]?.unitPrice) || 0)
                        )}
                        fullWidth
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 1, sm: 1 }} display="flex" alignItems="center">
                      {fields.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => remove(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Stack>
              <Button
                startIcon={<AddIcon />}
                onClick={() => append({ description: '', quantity: 1, unitPrice: 0 })}
                sx={{ mt: 1 }}
              >
                Add Line Item
              </Button>
              {errors.lineItems?.message && (
                <Typography color="error" variant="caption" display="block" mt={1}>
                  {errors.lineItems.message}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* GST & Totals */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="gstIncluded"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={field.value} onChange={field.onChange} />}
                      label="Include GST (10%)"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1} alignItems="flex-end">
                  <Box display="flex" justifyContent="space-between" width="100%" maxWidth={250}>
                    <Typography color="text.secondary">Subtotal:</Typography>
                    <Typography fontWeight={600}>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  {watchGstIncluded && (
                    <Box display="flex" justifyContent="space-between" width="100%" maxWidth={250}>
                      <Typography color="text.secondary">GST (10%):</Typography>
                      <Typography fontWeight={600}>{formatCurrency(gstAmount)}</Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" width="100%" maxWidth={250}>
                    <Typography variant="h6" fontWeight={700}>Total:</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatCurrency(total)}</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            {/* Notes */}
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes (optional)"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Payment terms, bank details reminder, thank you note..."
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />

            {/* Actions */}
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewOpen(true)}
              >
                Preview PDF
              </Button>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => navigate('/invoice')}>
                  Cancel
                </Button>
                <Button variant="outlined" onClick={handleSaveDraft} disabled={saving}>
                  {saving ? 'Saving…' : 'Save as Draft'}
                </Button>
                {!isEdit && (
                  <Button variant="contained" onClick={handleIssue} disabled={saving}>
                    {saving ? 'Saving…' : 'Issue Invoice'}
                  </Button>
                )}
                {isEdit && (
                  <Button variant="contained" onClick={handleSaveDraft} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Invoice Preview</DialogTitle>
        <DialogContent sx={{ minHeight: 500 }}>
          <InvoicePdfDocument invoice={previewData} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Inline Create Billing Profile Dialog */}
      <Dialog open={issuerDialogOpen} onClose={resetIssuerDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Billing Profile</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '75vh' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <IssuerProfileForm
              control={issuerControl}
              errors={issuerErrors}
              logoPreview={issuerLogoPreview}
              onLogoUpload={handleIssuerLogoUpload}
              onCopyProfile={handleCopyFromProfile}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetIssuerDialog}>Cancel</Button>
          <Button variant="contained" disabled={dialogSaving} onClick={handleIssuerCreate}>
            {dialogSaving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline Create Organisation Dialog */}
      <Dialog open={orgDialogOpen} onClose={() => setOrgDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Organisation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <OrganisationForm control={orgControl} errors={orgErrors} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={dialogSaving}
            onClick={handleOrgSubmit(async (data) => {
              setDialogSaving(true);
              try {
                const created = await orgService.create(data);
                const refreshed = await orgService.getAll();
                setOrganisations(refreshed);
                setValue('organisationId', created.id);
                setOrgDialogOpen(false);
              } catch (err) {
                setError(err.message || 'Failed to create organisation');
              } finally {
                setDialogSaving(false);
              }
            })}
          >
            {dialogSaving ? 'Creating…' : 'Add Organisation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceForm;
